import React, { useState } from "react";
import { Popover, Box, Button, Typography, useTheme, alpha } from "@mui/material";
import { ArrowDropDown, ExpandMoreOutlined } from "@mui/icons-material";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { DEFAULT_DAYS_ON_CHART } from "src/config";

const PRESET_FOR_DEFAULT_CHART_DAYS = {
  7: "Past 7 days",
  28: "Past 4 weeks",
};

const timelineButtons = [
  {
    name: "All",
    value: { start: null, end: null },
  },
  {
    name: "Today",
    value: {
      // start: new Date().setHours(0, 0, 0, 0),
      // end: new Date().setHours(23, 59, 59, 999),
      start: Date.now(),
      end: Date.now(),
    },
  },
  {
    name: "Yesterday",
    value: {
      // start: new Date(new Date().setDate(new Date().getDate() - 1)).setHours(0, 0, 0, 0),
      // end: new Date(new Date().setDate(new Date().getDate() - 1)).setHours(23, 59, 59, 999),
      start: new Date(new Date().getTime() - 24 * 60 * 60 * 1000),
      end: new Date(new Date().getTime() - 24 * 60 * 60 * 1000),
    },
  },
  {
    name: "Past 7 days",
    value: {
      start: new Date().setDate(new Date().getDate() - 7),
      end: Date.now(),
    },
  },
  {
    name: "Month to date",
    value: { start: new Date().setDate(1), end: Date.now() },
  },
  {
    name: "Past 4 weeks",
    value: {
      start: new Date().setDate(new Date().getDate() - 28),
      end: Date.now(),
    },
  },
  {
    name: "Past 3 months",
    value: {
      start: new Date().setMonth(new Date().getMonth() - 3),
      end: Date.now(),
    },
  },
  {
    name: "Past 6 months",
    value: {
      start: new Date().setMonth(new Date().getMonth() - 6),
      end: Date.now(),
    },
  },
  {
    name: "Past 12 months",
    value: {
      start: new Date().setMonth(new Date().getMonth() - 12),
      end: Date.now(),
    },
  },
  {
    name: "Custom",
    value: {
      start: "",
      end: "",
    },
  },
];

const TimelineDropdown = ({
  setDateRange,
  height = "40px",
  handleCustomRange,
  displayAll,
  loader,
  selectedTimeline,
  setSelectedTimeline,
  width = 'auto',
  variant = "default"
}) => {
  const theme = useTheme();
  const isAnalyticsVariant = variant === "analytics";
  const showAllOption = !isAnalyticsVariant;
  const defaultPresetName = isAnalyticsVariant
    ? PRESET_FOR_DEFAULT_CHART_DAYS[DEFAULT_DAYS_ON_CHART]
    : undefined;
  const emptyValue = {
    name: "All",
    value: { start: null, end: null },
  };
  const menuItems = showAllOption
    ? timelineButtons
    : timelineButtons.filter((b) => b.name !== "All");

  const initialTimeline = () => {
    if (selectedTimeline) return selectedTimeline;
    if (defaultPresetName) {
      const preset = menuItems.find((b) => b.name === defaultPresetName);
      if (preset) return preset;
    }
    if (displayAll) {
      return selectedTimeline || emptyValue;
    }
    return menuItems[0] ?? timelineButtons[0];
  };
  const [timeline, setTimeline] = useState(initialTimeline);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const spanRef = React.useRef();

  React.useEffect(() => {
    if (selectedTimeline) {
      setTimeline(selectedTimeline);
    }
  }, [selectedTimeline]);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
      <Button
        ref={spanRef}
        size="small"
        disabled={loader}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "left",
          fontSize: { xs: "12px", sm: "14px" }, // Adjust font size
          fontWeight: 500,
          lineHeight: "16.38px",
          color: theme.palette.text.primary,
          backgroundColor: "#fff",
          whiteSpace: "nowrap",
          px: { xs: 1, sm: 1.5 }, // Adjust padding
          mr: { xs: 0.5, sm: 1.3 },
          minWidth: { xs: '100px', sm: width }, // Use width prop for minWidth
          "&:hover": {
            backgroundColor: "#fff",
          },
          border: `1px solid ${open ? theme.palette.primary.main : "#E4E4E5"}`,
          height,
        }}
        onClick={handleClick}
        startIcon={<CalendarTodayRoundedIcon />}
        className="glass-dropdown"
      >
        {timeline?.name}
        <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
          <ArrowDropDown />
        </Box>
      </Button>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          mt: 0.5,
          maxWidth: "90vw", // Prevents overflow on small screens
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 1.1,
            width: { xs: "160px", sm: "200px" }, // Adjust width dynamically
          }}
        >
          {menuItems.map((item, i) => (
            <Button
              key={i}
              fullWidth
              sx={{
                py: 1,
                px: 2,
                borderRadius: "5px",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                color: theme.palette.text.primary,
                fontSize: "14px",
                fontWeight: "500",
                backgroundColor:
                  timeline?.name === item.name &&
                  alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                },
              }}
              onClick={() => {
                setTimeline(item);
                if (setSelectedTimeline) {
                  setSelectedTimeline(item);
                }
                if (item.name === "All") {
                  setDateRange({ from: null, to: null });
                } else if (item.name === "Custom") {
                  handleCustomRange(spanRef);
                } else {
                  setDateRange({
                    from: new Date(item.value.start),
                    to: new Date(item.value.end),
                  });
                }
                setAnchorEl(null);
              }}
            >
              {item.name}
            </Button>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};


export default TimelineDropdown;
